<template>
  <NsSlottedDropdown
    :key="dropdownList.length"
    :class="[
      $style.slottedDropdown,
      { NsSlottedDropdown__small: size === 'small' }
    ]"
    :label="label || translations.quantity"
    :selected="String(selectedValue)"
    :disabled="disabled"
    :show-custom-selected-item="showCustomSelectedItem"
    :error-message="errorMessage"
    :width="widthProp"
    :inline-label="inlineLabel"
    :translations="translations"
    show-all
    tall
    dense-list
    data-testid="quantitySelector"
    @select="handleSelect"
  >
    <div
      v-for="num in dropdownList"
      :key="num"
      :data-value="useInput(num) ? 'custom' : num"
    >
      {{ useInput(num) ? `${dropdownMax}+` : num }}
    </div>
    <template #custom>
      <input
        ref="input"
        v-model.number="quantityInput"
        :class="[$style.customInput, { [$style.small]: size === 'small' }]"
        type="number"
        min="0"
        step="1"
        @input="handleInput"
        @blur="handleBlur"
        @keypress="handleKeypress"
      />
    </template>
  </NsSlottedDropdown>
</template>

<script>
import { NsSlottedDropdown } from "@nuskin/design-components";
const DROPDOWN_MAX = 10;

/**
 * NsProductQuantitySelector
 *
 * How to use:
 *
 * @event select  When a selection occurs this event emits a function with
 * params: (value, index)
 */

export default {
  name: "NsProductQuantitySelector",
  components: { NsSlottedDropdown },
  props: {
    /**
     * Disables input
     */
    disabled: Boolean,
    /**
     * Number of options in the quantity selection dropdown
     */
    dropdownMax: {
      type: Number,
      default: DROPDOWN_MAX
    },
    /**
     * 	If true, the input will take up the full width of its container.
     */
    fullWidth: Boolean,
    /**
     * Include 0
     */
    includeZero: Boolean,
    /**
     * Whether the label should be inline
     */
    inlineLabel: Boolean,
    /**
     * Label (takes priority over translations.quantity)
     */
    label: {
      type: String,
      default: ""
    },
    /**
     * Max quantity available
     */
    maxAvailable: {
      type: Number,
      default: 999
    },
    /**
     * The selected quantity
     */
    selected: {
      type: String,
      default: ""
    },
    /**
     * The size of the selector.
     * @values small, large
     */
    size: {
      type: String,
      default: "large"
    },
    translations: {
      type: Object,
      default() {
        return {
          done: "Done",
          maxQuantityExceeded: "Low stock. Only %stock% left.",
          enter: "Enter",
          select: "Select",
          quantity: "Quantity"
        };
      }
    },
    /**
     * Adds 10px of width
     */
    wide: Boolean
  },
  data: function() {
    return {
      quantityInput: this.dropdownMax || 1,
      selectedValue: this.selected || 1,
      showCustomSelectedItem: false,
      //
      errorMessage: "",
      maxNotUsed: true
    };
  },
  computed: {
    dropdownList() {
      const start = this.includeZero ? 0 : 1;
      const end = Math.min(this.maxAvailable, this.dropdownMax);
      const list = [];
      for (var i = start; i <= end; i++) {
        list.push(i);
      }
      return list;
    },
    widthProp() {
      if (this.fullWidth) {
        return "100%";
      } else {
        return this.wide ? "70" : "60";
      }
    }
  },
  watch: {
    selected: {
      immediate: true,
      handler(newValue) {
        // handle parent changes that are above the dropdownMax
        if (this.useInput(+newValue)) {
          this.showCustomSelectedItem = true;
          this.quantityInput = newValue;
          this.handleInput({ target: { value: newValue } });
        } else {
          this.selectedValue = newValue;
        }
      }
    }
  },
  methods: {
    /**
     * Disables input
     */
    disable() {
      this.disabled = true;
    },
    /**
     * Enables input
     */
    enable() {
      this.disabled = false;
    },
    /**
     * Checks for invalid quantity
     */
    errorCheck() {
      if (this.maxAvailable && this.quantityInput > this.maxAvailable) {
        const maxText =
          this.translations.maxQuantityExceeded ||
          "Low stock. Only %stock% left.";
        this.errorMessage = maxText.replace("%stock%", this.maxAvailable);
      } else {
        this.errorMessage = "";
      }
    },
    handleBlur(event) {
      const value = event.target.value;

      if (!value) {
        this.quantityInput = 1;
        this.select(String(this.quantityInput));
      } else {
        this.select(value);
      }
    },
    handleInput(event) {
      const value = event.target.value;

      if (value.length > 3) {
        this.quantityInput = value.slice(0, 3);
      }

      this.select(String(this.quantityInput || 0));
      this.selectedValue = String(this.quantityInput || 0);
      this.showCustomSelectedItem = true;
      this.errorCheck();
    },
    handleKeypress(event) {
      if (event.key === "Enter") {
        this.$refs.input.blur();
      }
    },
    /**
     * Handle variant selection
     */
    handleSelect(value, index) {
      // this allows for showing custom html (via slot) by setting value to 'custom'
      if (value === "custom") {
        this.showCustomSelectedItem = true;
        this.errorCheck();
        this.quantityInput = this.dropdownMax || 1;
        this.select(
          this.dropdownMax,
          this.dropdownMax - this.includeZero ? 0 : 1
        );
        setTimeout(() => {
          this.$refs.input.focus();
          this.$refs.input.select();
        }, 100);
      } else {
        this.errorMessage = "";
        this.showCustomSelectedItem = false;
        this.quantityInput = this.dropdownMax || 1;
        this.select(value, index);
      }
    },
    select(value, index) {
      /**
       * Fired when a new quantity is selected: function(value: string|number, index: number) => void
       *
       * @type {select}
       * @property {(string|number)} value data-value attribute of the selected item, or index
       * @property {number} index index of the selected item
       */
      this.$emit("select", value, index);
    },
    useInput(itemQty) {
      return (
        itemQty >= this.dropdownMax && this.maxAvailable > this.dropdownMax
      );
    }
  }
};
</script>

<style lang="scss" module>
.slottedDropdown,
.slottedDropdown * {
  box-sizing: border-box;
  -webkit-font-smoothing: antialiased;
}

input.customInput,
input.customInput:focus,
input.customInput[type="number"],
input.customInput[type="number"]:focus {
  margin: 0;
  padding: 0;
  border: none !important;
  box-shadow: none;
  max-height: unset;
  min-height: unset;
  min-width: unset;
  width: 100%;
  /* Chrome, Safari, Edge, Opera */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  /* Firefox */
  -moz-appearance: textfield;

  &.small {
    height: 30px;
    text-align: center;
  }
}
</style>

<style lang="scss" scoped>
@media (max-width: 767px) {
  .NsSlottedDropdown__small {
    margin-top: 6px;
    width: 100%;

    ::v-deep .dropdown-area {
      flex-grow: 1;
    }

    ::v-deep .current-selection {
      min-height: 35px;
      height: 35px;
      border-radius: 6px;
    }

    ::v-deep .selected-item {
      flex-grow: 1;
      justify-content: center;
    }
  }
}
</style>
